import { withErrorBoundary } from '@sentry/react';
import React, { useMemo } from 'react';
import { assets } from '../../../assets';
import { BlurredContainerM2030 } from '../../../components/containers/containers';
import DefaultScreen from '../../../components/containers/default-screen';
import StyledReactSelect from '../../../components/inputs/styled-react-select';
import InfiniteList from '../../../components/lists/infinite-list';
import ListControls from '../../../components/lists/list-controls';
import ListHeader from '../../../components/lists/list-header';
import { Fallback } from '../../../components/utils/error-boundary';
import { formatDateTime } from '../../../helpers/date-formatters';
import { getStatusOptions, getExchangeOrderOrderingOptions } from '../../../helpers/type-options';
import ListItem from './components/exchange-order-list-item/exchange-order-list-item';
import useExchangeOrders from './hooks';
import { FormattedMessage, useIntl } from 'react-intl';

const ExchangeOrders = (): JSX.Element => {
  const intl = useIntl();
  const statusOptions = useMemo(() => {
    return getStatusOptions(intl);
  }, [intl]);
  const exchangeOrderOrderingOptions = useMemo(() => {
    return getExchangeOrderOrderingOptions(intl);
  }, [intl]);
  const { controls, list, account } = useExchangeOrders();
  const {
    itemsResultQuery,
    items,
    hasZeroItems,
    hasNextPage,
    fetchNextPage,
    sumPortfolioLastUpdateQueryResult,
  } = list;

  const {
    userAccountsQueryResult,
    userAccountOptions,
    selectedAccount,
    setSelectedAccount,
  } = account;

  const noContent = (
    <p className="self-center text-12 py-6 text-gray" style={{ height: 'calc(100vh - 240px)' }}>
      <FormattedMessage id="exchangeOrderListPage.emptyList" />
    </p>
  );

  return (
    <DefaultScreen
      background={assets.images.exchange_order_bg}
      title={intl.formatMessage({ id: 'exchangeOrderListPage.title' })}
      updatedAt={formatDateTime(sumPortfolioLastUpdateQueryResult.data?.sum_portfolio?.last_update)}
      rightContent={
        userAccountsQueryResult.data?.getUser ? (
          <StyledReactSelect
            placeholder={intl.formatMessage({
              id: 'exchangeOrderListPage.accountSelect.placeholder',
            })}
            type="blur"
            bold
            defaultValue={userAccountOptions[0]}
            options={userAccountOptions}
            onChange={(value) => {
              if (value !== selectedAccount) {
                controls.resetList();
                setSelectedAccount(value as string);
                controls.setSelectedInstruments([]);
              }
            }}
          />
        ) : undefined
      }
    >
      <BlurredContainerM2030 className="flex flex-col">
        <ListHeader
          createButtonLabel={intl.formatMessage({
            id: 'exchangeOrderListPage.header.newOrderButton',
          })}
          onCreateClick={controls.handleCreateClick}
        >
          <ListControls
            {...controls}
            statusOptions={statusOptions}
            orderingOptions={exchangeOrderOrderingOptions}
            istrumentPlaceholder={intl.formatMessage({
              id: 'exchangeOrderListPage.header.instrumentSelectPlaceholder',
            })}
            selectedInstruments={controls.selectedSecurities}
            instrumentOptions={
              controls.securitiesQueryResult.data?.get_securities.map((s) => ({
                value: s.short_name,
                label: s.name,
              })) || []
            }
          />
        </ListHeader>

        {hasZeroItems ? (
          noContent
        ) : (
          <InfiniteList
            hasNextPage={hasNextPage}
            isNextPageLoading={itemsResultQuery.fetching}
            items={items}
            loadNextPage={fetchNextPage}
            listItem={ListItem}
            desktopItemHeight={84}
            mobileItemHeight={185}
          />
        )}
      </BlurredContainerM2030>
    </DefaultScreen>
  );
};

export default withErrorBoundary(ExchangeOrders, { fallback: Fallback });
